import React, { useState, useEffect } from 'react';
import styles from './styles/deliveryCRM.module.scss';
import { mockDeliveryRates } from '../../Data/services/deliveryMockData';
import { useGetDeliveryPricingQuery, useUpdateDeliveryPricingMutation } from '../../Data/services/deliveryApi';
import Toast, { SuccessToast } from '../../Hooks/useToast';
import CustomLoader from '../../Components/CustomComponents/CustomLoader';

const DeliveryCRMScreen = () => {
  const [deliveryRates, setDeliveryRates] = useState(mockDeliveryRates);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  // API hooks
  const { data: apiData, isLoading, error, refetch } = useGetDeliveryPricingQuery();
  const [updatePricing, { isLoading: isUpdating }] = useUpdateDeliveryPricingMutation();

  // Sync API data with local state
  useEffect(() => {
    if (apiData?.data) {
      const apiPricing = apiData.data;
      setDeliveryRates(prev => ({
        ...prev,
        // Map API fields to local state fields
        priceForFirst2km: apiPricing.price_for_first_2km ?? '',
        priceFor100m: apiPricing.price_for_100m ?? '',
      }));
    }
  }, [apiData]);

  const handleInputChange = (field, value) => {
    // Allow empty values to be set (for clearing the field)
    setDeliveryRates(prev => ({
      ...prev,
      [field]: value === '' ? '' : value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const pricingData = {
        price_for_first_2km: deliveryRates.priceForFirst2km === '' ? 0 : deliveryRates.priceForFirst2km,
        price_for_100m: deliveryRates.priceFor100m === '' ? 0 : deliveryRates.priceFor100m
      };

      const result = await updatePricing(pricingData).unwrap();
      
      if (result.code === 200) {
        SuccessToast(result.message || 'Delivery pricing updated successfully!');
        setIsEditing(false);
        // Refetch the data to get updated values
        refetch();
      }
    } catch (error) {
      console.error('Error updating delivery pricing:', error);
      Toast(error?.data?.message || 'Failed to update delivery pricing', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to API data if available, otherwise use mock data
    if (apiData?.data) {
      const apiPricing = apiData.data;
      setDeliveryRates(prev => ({
        ...prev,
        priceForFirst2km: apiPricing.price_for_first_2km ?? '',
        priceFor100m: apiPricing.price_for_100m ?? '',
      }));
    } else {
      setDeliveryRates(mockDeliveryRates);
    }
    setIsEditing(false);
  };

  const calculateDeliveryCost = (distance) => {
    const basePrice = deliveryRates.priceForFirst2km === '' || deliveryRates.priceForFirst2km === undefined || deliveryRates.priceForFirst2km === null ? 0 : deliveryRates.priceForFirst2km;
    const pricePer100m = deliveryRates.priceFor100m === '' || deliveryRates.priceFor100m === undefined || deliveryRates.priceFor100m === null ? 0 : deliveryRates.priceFor100m;
    
    if (distance <= 2) {
      return basePrice;
    }
    
    const extraDistance = distance - 2; // Beyond first 2km
    const extraDistanceIn100M = Math.ceil(extraDistance * 10); // Convert km to 100m units
    return basePrice + (extraDistanceIn100M * pricePer100m);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className={styles.MainContainer}>
        <CustomLoader />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={styles.MainContainer}>
        <div className={styles.headerContainer}>
          <div className={styles.titleContainer}>
            <h2>Delivery CRM</h2>
          </div>
        </div>
        <div className={styles.errorContainer}>
          <h3>Error Loading Delivery Pricing</h3>
          <p>{error?.data?.message || error?.message || 'Failed to load delivery pricing data'}</p>
          <button onClick={() => refetch()} className="btn primary-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.MainContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.titleContainer}>
          <h2>Delivery CRM</h2>
          {apiData && (
            <div className={styles.apiStatus}>
              <span className={styles.statusIndicator}>●</span>
              <span>Live Data</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.ratesCard}>
        <div className={styles.cardHeader}>
          <h3>Delivery Rate Configuration</h3>
          {!isEditing ? (
            <button 
              className={`btn primary-btn ${styles.editBtn}`}
              onClick={() => setIsEditing(true)}
            >
              Edit Rates
            </button>
          ) : (
            <div className={styles.actionButtons}>
              <button 
                className={`btn ${styles.cancelBtn}`}
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className={`btn primary-btn ${styles.saveBtn}`}
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>

        <div className={styles.ratesForm}>
          <div className={styles.formGroup}>
            <label>Price for First 2km</label>
            <input
              type="number"
              value={deliveryRates.priceForFirst2km === undefined || deliveryRates.priceForFirst2km === null ? '' : deliveryRates.priceForFirst2km}
              onChange={(e) => handleInputChange('priceForFirst2km', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
              disabled={!isEditing}
              min="0"
              step="0.01"
              className={styles.numberInput}
            />
            <small>Base price for deliveries within the first 2 kilometers</small>
          </div>

          <div className={styles.formGroup}>
            <label>Price per 100 meters</label>
            <input
              type="number"
              value={deliveryRates.priceFor100m === undefined || deliveryRates.priceFor100m === null ? '' : deliveryRates.priceFor100m}
              onChange={(e) => handleInputChange('priceFor100m', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
              disabled={!isEditing}
              min="0"
              step="0.01"
              className={styles.numberInput}
            />
            <small>Additional charge for every 100 meters beyond the base distance</small>
          </div>
        </div>
      </div>

      <div className={styles.previewCard}>
        <h3>Delivery Cost Preview</h3>
        <div className={styles.previewGrid}>
          {[0.5, 1, 1.5, 2, 2.5, 3, 4, 5].map(distance => {
            const cost = calculateDeliveryCost(distance);
            const isBasePrice = distance <= 2;
            return (
              <div key={distance} className={styles.previewItem}>
                <div className={styles.distance}>{distance} km</div>
                <div className={styles.cost}>
                  GHS {cost.toFixed(2)}
                </div>
                {isBasePrice && cost === 0 && (
                  <div className={styles.freeLabel}>FREE</div>
                )}
                {isBasePrice && cost > 0 && (
                  <div className={styles.baseLabel}>BASE</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DeliveryCRMScreen;
